/**
 * Description du fichier:
 * Utilitaire de conversion entre les différents formats de devis utilisés dans l'application.
 * 
 * Relations avec d'autres fichiers:
 * - Importe les types Devis et ArticleDevis depuis ../services/types.ts
 * - Importe les types Devis et Article depuis ../types/dimensions.ts
 * - Utilisé par DevisService pour convertir les devis entre les différents formats
 * 
 * Actions déclenchées:
 * - Convertit un devis du format services/types.ts vers le format types/dimensions.ts
 * - Convertit un devis du format types/dimensions.ts vers le format services/types.ts
 * - Assure la compatibilité entre les différentes représentations des devis dans l'application
 * - Gère les cas particuliers comme la détermination de la forme des articles
 */

import { Devis as DevisService, ArticleDevis, GroupeMatiere as GroupeMatiereService } from '../services/types';
import { Devis as DevisDimensions, Article, GroupeMatiere as GroupeMatiereDimensions } from '../types/dimensions';

  /**
   * Convertit un Devis du format services/types.ts vers le format types/dimensions.ts
   */
  export function convertToDevisDimensions(devis: DevisService): DevisDimensions {
    // Convertir les articles en excluant les articles libres (hors calepinage)
    const articles: Article[] = devis.articles
      .filter(article => !article.estArticleLibre)
      .map(article => {
      // Déterminer la forme à utiliser
      let forme: 'rectangulaire_bandeau' | 'rectangulaire_demi_bandeau' | 'rectangulaire_joues' | 'rond_bandeau' | 'rond_avec_bandeau' | 'cylindre_joues' | 'rectangle_simple' | 'rectangle_boite' | 'rectangle_joues_biseau' | 'rectangle_demi_bandeau_biseau' | undefined = undefined;
      
      // Si l'article a une forme définie, l'utiliser
      if (article.forme) {
        if (article.forme === 'rectangulaire_bandeau' || 
            article.forme === 'rectangulaire_joues' || 
            article.forme === 'rectangulaire_demi_bandeau' ||  // AJOUTÉ
            article.forme === 'rond_bandeau' || 
            article.forme === 'rond_avec_bandeau' ||  // AJOUTÉ
            article.forme === 'cylindre_joues' ||
            article.forme === 'rectangle_simple' ||
            article.forme === 'rectangle_boite' ||  // AJOUTÉ
            article.forme === 'rectangle_joues_biseau' ||
            article.forme === 'rectangle_demi_bandeau_biseau') {
          forme = article.forme as any;
        }
      }
      
      // Si l'article a un prixCalcule avec article_forme défini, l'utiliser
      if (article.prixCalcule && article.prixCalcule.article_forme) {
        if (article.prixCalcule.article_forme === 'rectangulaire_bandeau' || 
            article.prixCalcule.article_forme === 'rectangulaire_joues' || 
            article.prixCalcule.article_forme === 'rectangulaire_demi_bandeau' ||  // AJOUTÉ
            article.prixCalcule.article_forme === 'rond_bandeau' || 
            article.prixCalcule.article_forme === 'rond_avec_bandeau' ||  // AJOUTÉ
            article.prixCalcule.article_forme === 'cylindre_joues' ||
            article.prixCalcule.article_forme === 'rectangle_simple' ||
            article.prixCalcule.article_forme === 'rectangle_boite' ||  // AJOUTÉ
            article.prixCalcule.article_forme === 'rectangle_joues_biseau' ||
            article.prixCalcule.article_forme === 'rectangle_demi_bandeau_biseau') {
          forme = article.prixCalcule.article_forme as any;
        }
      }
      
      // Si aucune forme n'est définie, afficher une erreur
      if (!forme) {
        console.error("ERREUR: La propriété 'forme' n'est pas définie pour l'article:", article.nom);
      }
      
      return {
      id: article.id || `article-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      nom: article.nom,
      forme: forme, // Ajouter la forme directement à l'article
      dimensions: {
        ...article.dimensions,
        forme: forme // Ajouter la forme aux dimensions aussi pour compatibilité
      },
      materiaux: article.materiaux,
      options: {
        fond: article.options.fond,
        grille: article.options.grille,
        fermeture: article.options.fermeture,
        mousse: article.options.mousse,
        surpiqure: article.options.surpiqure,
        formeComplexe: article.options.formeComplexe
      },
      quantite: article.quantite,
      prix: article.prixUnitaire,
      prixUnitaire: article.prixUnitaire,
      estArticleLibre: article.estArticleLibre,
      typeArticleLibre: article.typeArticleLibre
    };
  });

  // Convertir les groupes de matières
  const groupesMatiere: GroupeMatiereDimensions[] = devis.groupesMatiere.map(groupe => ({
    matiere: groupe.matiere,
    couleur: groupe.couleur,
    laize: groupe.laize,
    prixMetreLineaire: groupe.prixMetreLineaire,
    articles: articles.filter(a => 
      a.materiaux.dessus.matiere === groupe.matiere && 
      a.materiaux.dessus.couleur === groupe.couleur
    ),
    pieces: groupe.pieces,
    calepinage: groupe.calepinage,
    pourcentageEconomie: groupe.pourcentageEconomie
  }));

  // Créer le devis au format dimensions
  return {
    id: devis.id,
    reference: devis.reference,
    client: devis.client,
    date: devis.date,
    projet: devis.projet,
    articles: articles,
    groupesMatiere: groupesMatiere,
    coutMatiereTotal: devis.coutMatiereTotal,
    coutMainOeuvreTotal: devis.coutMainOeuvreTotal,
    coutAccessoiresTotal: devis.coutAccessoiresTotal,
    coutPetitesFournitures: devis.coutPetitesFournitures,
    montantTotal: devis.montantTotal,
    economieTotal: devis.economieTotal,
    tauxOptimisationMoyen: devis.tauxOptimisationMoyen,
    soumisATVA: devis.soumisATVA,
    tauxTVA: devis.tauxTVA,
    montantTVA: devis.montantTVA
  };
}

/**
 * Convertit un Devis du format types/dimensions.ts vers le format services/types.ts
 */
export function convertToDevisService(devis: DevisDimensions): DevisService {
  // Convertir les articles
  const articles: ArticleDevis[] = devis.articles.map(article => ({
    id: article.id,
    nom: article.nom,
    type: 'coussin', // Valeur par défaut
    forme: article.dimensions.forme || 'rectangulaire_bandeau', // Utiliser une valeur par défaut si forme n'est pas définie
    dimensions: {
      longueur: article.dimensions.longueur,
      largeur: article.dimensions.largeur,
      hauteur: article.dimensions.hauteur,
      diametre: article.dimensions.diametre,
      talon: article.dimensions.talon
    },
    materiaux: article.materiaux,
    options: {
      fond: article.options.fond,
      grille: article.options.grille || false,
      mousse: article.options.mousse || 'souple',
      fermeture: article.options.fermeture || 'glissiere',
      surpiqure: article.options.surpiqure || false,
      formeComplexe: article.options.formeComplexe || false
    },
    quantite: article.quantite,
    prixUnitaire: article.prixUnitaire || article.prix || 0,
    estArticleLibre: article.estArticleLibre,
    typeArticleLibre: article.typeArticleLibre
  }));

  // Convertir les groupes de matières
  const groupesMatiere: GroupeMatiereService[] = devis.groupesMatiere.map(groupe => ({
    matiere: groupe.matiere,
    couleur: groupe.couleur,
    laize: groupe.laize,
    prixMetreLineaire: groupe.prixMetreLineaire,
    articles: articles.filter(a => 
      a.materiaux.dessus.matiere === groupe.matiere && 
      a.materiaux.dessus.couleur === groupe.couleur
    ),
    pieces: groupe.pieces,
    calepinage: groupe.calepinage,
    pourcentageEconomie: groupe.pourcentageEconomie
  }));

  // Créer le devis au format service
  return {
    id: devis.id,
    reference: devis.reference,
    client: devis.client,
    date: devis.date,
    projet: devis.projet,
    articles: articles,
    groupesMatiere: groupesMatiere,
    coutMatiereTotal: devis.coutMatiereTotal,
    coutMainOeuvreTotal: devis.coutMainOeuvreTotal,
    coutAccessoiresTotal: devis.coutAccessoiresTotal,
    coutPetitesFournitures: devis.coutPetitesFournitures,
    montantTotal: devis.montantTotal,
    economieTotal: devis.economieTotal,
    tauxOptimisationMoyen: devis.tauxOptimisationMoyen,
    soumisATVA: devis.soumisATVA,
    tauxTVA: devis.tauxTVA,
    montantTVA: devis.montantTVA
  };
}
